import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  isOpen?: boolean;
  isCollapsed?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, isCollapsed = false, onClose }) => {
  const { isSystemAdmin, isTenantAdmin, hasPermission, user } = useAuth();

  // Explicit tenant permission check (ignores tenant-admin auto-allow)
  const hasExplicitTenantPermission = React.useMemo(() => {
    const tenantPerms = new Set([
      'tenant.list',
      'tenant.get',
      'tenant.update',
      'tenant.create',
      'tenant.delete',
    ]);
    const perms = user?.permissions || [];
    return perms.some((p) => {
      const permName = (p as any).name || (p as any).id;
      return permName && tenantPerms.has(permName);
    });
  }, [user]);

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      <button className="sidebar-close" onClick={onClose} aria-label="Close menu">
        ×
      </button>
      <nav className="sidebar-nav">
        {/* General Section */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">General</div>
          <ul className="sidebar-menu">
            {(hasPermission('tracking.view') ||
              hasPermission('tracking.livemap') ||
              hasPermission('tracking.lifecycle')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  end
                  title="Dashboard"
                >
                  <span className="sidebar-menu-icon">📊</span>
                  <span className="sidebar-menu-text">Dashboard</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Dashboard</span>
                  )}
                </NavLink>
              </li>
            )}
            {(isSystemAdmin || hasExplicitTenantPermission) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/tenants"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Tenants"
                >
                  <span className="sidebar-menu-icon">🏢</span>
                  <span className="sidebar-menu-text">Tenants</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Tenants</span>
                  )}
                </NavLink>
              </li>
            )}
          </ul>
        </div>

        {/* Management Section */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Management</div>
          <ul className="sidebar-menu">
            {/* Ambulances */}
            {(hasPermission('ambulance.list') ||
              hasPermission('ambulance.get')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/ambulances"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Ambulances"
                >
                  <span className="sidebar-menu-icon">🚑</span>
                  <span className="sidebar-menu-text">Ambulances</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Ambulances</span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Appointments */}
            {/* {(hasPermission('appointment.read') ||
              user?.permissions?.some((p) => p.name === 'appointment.read') ||
              user?.permissions?.some((p) => p.id === 'appointment.read') ||
              isSystemAdmin ||
              isTenantAdmin) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/appointments"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                >
                  <span className="sidebar-menu-icon">📅</span>
                  Appointments
                </NavLink>
              </li>
            )} */}

            {/* Bookings */}
            {(hasPermission('booking.list') ||
              hasPermission('booking.get')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/bookings"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Bookings"
                >
                  <span className="sidebar-menu-icon">📋</span>
                  <span className="sidebar-menu-text">Bookings</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Bookings</span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Patients */}
            {(hasPermission('patient.list') ||
              hasPermission('patient.get')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/patients"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Patients"
                >
                  <span className="sidebar-menu-icon">🏥</span>
                  <span className="sidebar-menu-text">Patients</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Patients</span>
                  )}
                </NavLink>
              </li>
            )}

            {(hasPermission('user.list') ||
              hasPermission('user.get')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/users"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Staff"
                >
                  <span className="sidebar-menu-icon">👤</span>
                  <span className="sidebar-menu-text">Staff</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Staff</span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Reports */}
            {hasPermission('report.read') && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/reports"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Reports"
                >
                  <span className="sidebar-menu-icon">📄</span>
                  <span className="sidebar-menu-text">Reports</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Reports</span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Fuel */}
            {(isTenantAdmin || hasPermission('fuel.view')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/fuel"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Fuel"
                >
                  <span className="sidebar-menu-icon">⛽</span>
                  <span className="sidebar-menu-text">Fuel</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Fuel</span>
                  )}
                </NavLink>
              </li>
            )}

            {/* Attendance - Available to users with attendance permissions */}
            {hasPermission('attendance.list') && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/attendance"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Attendance"
                >
                  <span className="sidebar-menu-icon">⏰</span>
                  <span className="sidebar-menu-text">Attendance</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Attendance</span>
                  )}
                </NavLink>
              </li>
            )}
          </ul>
        </div>

        {/* Settings Section */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Settings</div>
          <ul className="sidebar-menu">
            {/* Settings - Combined page */}
            {(hasPermission('ambulancetype.list') ||
              hasPermission('gpsdevice.list') ||
              hasPermission('area.list') ||
              hasPermission('permission.list') ||
              hasPermission('role.list') ||
              hasPermission('staffcategory.list') ||
              hasPermission('patientvitalfield.list') ||
              hasPermission('tenant.update') ||
              hasPermission('profile.view')) && (
              <li className="sidebar-menu-item">
                <NavLink
                  to="/dashboard/settings"
                  className={({ isActive }) =>
                    `sidebar-menu-link ${isActive ? 'active' : ''}`
                  }
                  title="Settings"
                >
                  <span className="sidebar-menu-icon">⚙️</span>
                  <span className="sidebar-menu-text">Settings</span>
                  {isCollapsed && (
                    <span className="sidebar-tooltip">Settings</span>
                  )}
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;

